/**
 * Hero → Introduction scroll scene contract (see docs/04-motion.md).
 *
 * The scene is a client island (`HeroScrollScene`) that scrubs the swirl
 * circle over the pinned hero and reveals the Introduction. Headline / aside /
 * body reveal attrs live in `sectionHeaderReveal.ts`; this file keeps hero
 * chrome, swirl clip, and runway media-query contracts.
 */

import { PROSE_BLOCK_REVEAL_ATTR } from '@/constants/proseBlockReveal';
import {
  SECTION_ASIDE_REVEAL_ATTR,
  SECTION_BODY_REVEAL_ATTR,
} from '@/constants/sectionHeaderReveal';

/** Marks chrome that fades with the intro content timeline (ghost, graphic, StickyNav). */
export const HERO_REVEAL_ATTR = 'data-hero-reveal';

export const HERO_REVEAL_SELECTOR = `[${HERO_REVEAL_ATTR}]`;

/** Spread onto a server-rendered element to include it in the chrome reveal. */
export const HERO_REVEAL_PROPS = { [HERO_REVEAL_ATTR]: '' } as const;

/**
 * Ancestors that gate child entrance animations via opacity/visibility while
 * a GSAP scrub / play-once reveal is in progress (`useDiscernibleInView`).
 */
export const REVEAL_ROOT_ATTRS = [
  HERO_REVEAL_ATTR,
  SECTION_ASIDE_REVEAL_ATTR,
  SECTION_BODY_REVEAL_ATTR,
  PROSE_BLOCK_REVEAL_ATTR,
] as const;

/** StickyNav tracking contract id — the section the scene reveals into. */
export const HERO_SCENE_INTRO_ID = 'introduction';

/**
 * SVG `<clipPath>` id for the swirl mask applied to `#introduction` during
 * the circle travel window. Must match `HeroSwirlClipDef`.
 */
export const HERO_SWIRL_CLIP_ID = 'hero-swirl-clip';

/**
 * Separate `<clipPath>` id for StickyNav — same swirl geometry, but its path
 * transform is synced in the nav's local coordinate space (`userSpaceOnUse`
 * is per clipped element, so intro and nav cannot share one path transform).
 */
export const HERO_SWIRL_CLIP_NAV_ID = 'hero-swirl-clip-nav';

/**
 * Marks StickyNav as a swirl clip target. The scene finds it with
 * `HERO_SWIRL_CLIP_TARGET_SELECTOR` (StickyNav sits outside `#introduction`).
 */
export const HERO_SWIRL_CLIP_TARGET_ATTR = 'data-hero-swirl-clip-target';

export const HERO_SWIRL_CLIP_TARGET_SELECTOR = `[${HERO_SWIRL_CLIP_TARGET_ATTR}]`;

/** Spread onto StickyNav so the hero scene can clip it to the swirl. */
export const HERO_SWIRL_CLIP_TARGET_PROPS = {
  [HERO_SWIRL_CLIP_TARGET_ATTR]: '',
} as const;

/**
 * Present on `#introduction` only while GSAP has the swirl clip applied.
 * Gates the off-white upward bleed in `Introduction.module.css` — without the
 * clip, the unclipped bleed would paint a solid sheet over the hero at first
 * paint (before the gsap chunk attaches) and after travel completes.
 */
export const HERO_SWIRL_CLIP_ACTIVE_ATTR = 'data-hero-swirl-clip';

/**
 * Set on `#introduction` once circle travel finishes and the swirl clip is
 * released. Together with the absence of `HERO_SWIRL_CLIP_ACTIVE_ATTR`, this
 * lets CSS distinguish "pre-GSAP / awaiting clip" (hide the fully-pulled-up
 * intro so it does not cover the hero) from "post-travel" (intro paints
 * normally, unclipped).
 */
export const HERO_SWIRL_SCENE_DONE_ATTR = 'data-hero-swirl-done';

/**
 * Matches viewports below Tailwind `md` (48rem). Used by the hero scroll
 * scene for narrow dual-tween travel (ease-in `y`, linear scale/rotation).
 */
export const HERO_SCENE_NARROW_MEDIA = '(width < 48rem)' as const;

/**
 * Max wait before attaching the intro SplitText / content scrub after the
 * swirl scene is ready. `requestIdleCallback` prefers idle time so early
 * swirl scrub does not compete with SplitText + `ScrollTrigger.refresh`.
 */
export const HERO_SCENE_CONTENT_REVEAL_IDLE_TIMEOUT_MS = 1200;

/**
 * Marks the hero background video host so the scroll scene can pause decode
 * while the swirl covers the viewport (resume if the user scrubs back).
 */
export const HERO_BACKGROUND_VIDEO_ATTR = 'data-hero-background-video';

export const HERO_BACKGROUND_VIDEO_SELECTOR = `[${HERO_BACKGROUND_VIDEO_ATTR}]`;

export const HERO_BACKGROUND_VIDEO_PROPS = {
  [HERO_BACKGROUND_VIDEO_ATTR]: '',
} as const;

/**
 * Proxy layer `will-change` while live clip sync runs. Cleared to `auto` after
 * travel so the rest of the page does not keep a lasting compositor hint.
 */
export const HERO_SWIRL_PROXY_WILL_CHANGE_ACTIVE = 'transform' as const;

export const HERO_SWIRL_PROXY_WILL_CHANGE_IDLE = 'auto' as const;
